import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["icon", "label", "spinner"]
  static values = {
    url: String,
    errorMessage: String,
    clipboardErrorMessage: String,
    successMessage: String
  }

  connect() {
    this.isLoading = false
  }

  copy(event) {
    event.preventDefault()
    if (this.isLoading) return

    this.setLoadingState(true)

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      credentials: "same-origin"
    })
      .then((response) => this.handleResponse(response))
      .then((data) => this.copyToClipboard(data.login_url).then(() => data))
      .then((data) => {
        const message = data.message || this.successMessageValue || "Link copied"
        this.showNotification(message, "success")
      })
      .catch((error) => {
        const message = error?.message || this.errorMessageValue || "Unable to generate link"
        this.showNotification(message, "danger")
      })
      .finally(() => {
        this.setLoadingState(false)
      })
  }

  handleResponse(response) {
    if (!response.ok) {
      return response
        .json()
        .catch(() => ({}))
        .then((data) => {
          const message = data.message || this.errorMessageValue || response.statusText
          throw new Error(message)
        })
    }
    return response.json()
  }

  copyToClipboard(text) {
    if (!text) {
      return Promise.reject(new Error(this.errorMessageValue))
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(() => {
        throw new Error(this.clipboardErrorMessageValue || this.errorMessageValue)
      })
    }

    return new Promise((resolve, reject) => {
      const textarea = document.createElement("textarea")
      textarea.value = text
      textarea.style.position = "fixed"
      textarea.style.opacity = "0"
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()

      try {
        const successful = document.execCommand("copy")
        document.body.removeChild(textarea)
        if (successful) {
          resolve()
        } else {
          reject(new Error(this.clipboardErrorMessageValue || this.errorMessageValue))
        }
      } catch (error) {
        document.body.removeChild(textarea)
        reject(new Error(this.clipboardErrorMessageValue || this.errorMessageValue || error.message))
      }
    })
  }

  setLoadingState(loading) {
    this.isLoading = loading
    this.element.toggleAttribute("disabled", loading)
    this.element.classList.toggle("disabled", loading)

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("d-none", !loading)
    }

    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("d-none", loading)
    }
  }

  showNotification(message, type = "info") {
    const notification = document.createElement("div")
    notification.className = `alert alert-${type} position-fixed`
    notification.style.cssText = "top: 20px; right: 20px; z-index: 1080; opacity: 0; transition: opacity 0.3s;"
    notification.textContent = message

    document.body.appendChild(notification)

    requestAnimationFrame(() => {
      notification.style.opacity = "1"
    })

    setTimeout(() => {
      notification.style.opacity = "0"
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }, 300)
    }, 2500)
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
  }
}
